import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import AdminLayout from '../../components/AdminLayout'
import { labelVentana, VENTANAS_HORARIAS, VENTANAS_SISTEMA } from '../../lib/ventanasHorarias'
import {
  getEntregas, marcarEnRuta, actualizarBultos, getDiaRepartoLabel, getFechaReparto,
  isAfterCutoff, yaPasoCorteD1, CUTOFF_HOUR, ESTADO_LABEL, ESTADO_COLOR,
  getUsuariosReparto, asignarRepartidor, reagendarEntrega, getEntregaFotoSignedUrl,
  quienRecibe, quienPaga, esRegalo,
  getKitTemplateItems, getProductosCatalogo, calcularExtrasDesdeEdicion,
  guardarEdicionKit, getHistorialKitEntrega, netearComposicion
} from '../../lib/entregasApi'
import { getCapacidadDiaria } from '../../lib/cobrosApi'
import { formatearTelUY } from '../../lib/documentos'

export default function Entregas() {
  const [fecha, setFecha] = useState(getFechaReparto())
  // Fix #2 (7-jul-2026): rango de días. 'dia' = comportamiento clásico (1 fecha), 'proximos7' = 7 días desde hoy, 'semana' = lun-dom
  const [modoRango, setModoRango] = useState('dia')
  const [entregas, setEntregas] = useState([])
  const [usuarios, setUsuarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [seleccionadas, setSeleccionadas] = useState(new Set())
  const [enviando, setEnviando] = useState(false)
  const [filtroRep, setFiltroRep] = useState('todos')  // todos | sin_asignar | <userId>
  const [filtroEstado, setFiltroEstado] = useState('activas') // activas | todas | programada | en_ruta | entregada
  const [mostrarArchivadas, setMostrarArchivadas] = useState(false)
  const [ocultarTests, setOcultarTests] = useState(true)  // coherencia es_test: ocultar entregas de prueba por default
  const [busqueda, setBusqueda] = useState('')
  const [scanOpen, setScanOpen] = useState(false)   // panel de carga por escaneo (QR etiqueta)
  const [scanFeed, setScanFeed] = useState([])      // feedback de últimos escaneos
  const [armandoEntrega, setArmandoEntrega] = useState(null) // entrega abierta en modal
  const [editandoKit, setEditandoKit] = useState(null) // entrega abierta en editor de kit
  const [capacidad, setCapacidad] = useState(20)
  const [mlPendientes, setMlPendientes] = useState(0)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const clienteFiltradoId = searchParams.get('cliente')

  // Deep-links del dashboard: ?fecha=hoy, ?sin_repartidor=1
  useEffect(() => {
    if (searchParams.get('fecha') === 'hoy') {
      setFecha(new Date().toISOString().slice(0, 10))
      setModoRango('dia')
    }
    if (searchParams.get('sin_repartidor') === '1') {
      setFiltroRep('sin_asignar')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { load() }, [fecha, modoRango])
  // Reset de selección al cambiar de vista → evita la "selección fantasma"
  // (quedaban entregas seleccionadas fuera del filtro actual).
  useEffect(() => { setSeleccionadas(new Set()) }, [fecha, modoRango, filtroEstado, filtroRep, clienteFiltradoId])
  useEffect(() => {
    getUsuariosReparto().then(setUsuarios)
    getCapacidadDiaria().then(setCapacidad)
    // Widget: ML pendientes de despacho (canal separado)
    supabase.from('ml_ventas').select('id', { count: 'exact', head: true })
      .eq('status', 'paid').is('date_shipped', null)
      .then(({ count }) => setMlPendientes(count || 0))
  }, [])

  async function load() {
    setLoading(true)
    let param = fecha
    if (modoRango === 'proximos7') {
      const desde = new Date()
      const hasta = new Date()
      hasta.setDate(hasta.getDate() + 6)
      param = { desde: desde.toISOString().slice(0, 10), hasta: hasta.toISOString().slice(0, 10) }
    } else if (modoRango === 'semana') {
      // Semana ISO: lunes a domingo de la semana de "fecha"
      const d = new Date(fecha + 'T12:00:00')
      const dow = d.getDay() // 0 dom, 1 lun ... 6 sab
      const offsetLun = (dow + 6) % 7  // días desde lunes
      const lun = new Date(d); lun.setDate(d.getDate() - offsetLun)
      const dom = new Date(lun); dom.setDate(lun.getDate() + 6)
      param = { desde: lun.toISOString().slice(0, 10), hasta: dom.toISOString().slice(0, 10) }
    }
    const data = await getEntregas(param)
    setEntregas(data)
    setLoading(false)
  }

  async function cambiarAsignado(entregaId, repartidorId) {
    const v = repartidorId || null
    setEntregas(es => es.map(e => e.id === entregaId ? {
      ...e,
      repartidor_id: v,
      repartidor: v ? (usuarios.find(u => u.id === v) ? { nombre: usuarios.find(u => u.id === v).nombre, role: usuarios.find(u => u.id === v).role } : null) : null
    } : e))
    try { await asignarRepartidor(entregaId, v) } catch (err) { console.error(err); load() }
  }

  async function asignarSeleccionadasA(repartidorId) {
    if (seleccionadas.size === 0) { alert('Seleccioná entregas primero.'); return }
    if (!confirm(`Asignar ${seleccionadas.size} entrega${seleccionadas.size !== 1 ? 's' : ''} a este repartidor?`)) return
    try {
      await asignarRepartidor([...seleccionadas], repartidorId || null)
      await load()
    } catch (err) { alert('Error: ' + err.message) }
  }

  function todayStr() {
    return new Date().toISOString().slice(0, 10)
  }
  function tomorrowStr() {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    return d.toISOString().slice(0, 10)
  }

  function toggle(id) {
    const n = new Set(seleccionadas)
    n.has(id) ? n.delete(id) : n.add(id)
    setSeleccionadas(n)
  }

  // Seleccionar/deseleccionar todo lo VISIBLE (entregasActivas filtradas), no el crudo.
  function toggleAll() {
    const ids = entregasActivas.map(e => e.id)
    const todasSel = ids.length > 0 && ids.every(id => seleccionadas.has(id))
    setSeleccionadas(todasSel ? new Set() : new Set(ids))
  }
  function limpiarSeleccion() { setSeleccionadas(new Set()) }

  async function salirARuta() {
    // Solo afecta programadas, aunque haya otras seleccionadas
    const programadasSel = entregas
      .filter(e => seleccionadas.has(e.id) && e.estado === 'programada')
      .map(e => e.id)
    if (programadasSel.length === 0) {
      alert('No hay entregas programadas seleccionadas.')
      return
    }
    if (!confirm(`Marcar ${programadasSel.length} entregas como "en ruta" y notificar a los clientes por WhatsApp?`)) return
    setEnviando(true)
    try {
      await marcarEnRuta(programadasSel)
      await load()
      setSeleccionadas(new Set())
      alert('Listo. WhatsApp enviado a los clientes.')
    } catch (e) {
      alert('Error: ' + e.message)
    } finally {
      setEnviando(false)
    }
  }

  async function imprimirEtiquetas() {
    if (seleccionadas.size === 0) {
      alert('Seleccioná al menos una entrega para imprimir su etiqueta.')
      return
    }
    const ids = [...seleccionadas]
    try { await supabase.rpc('admin_marcar_etiqueta_impresa', { p_ids: ids }) } catch (err) { console.error('marcar etiquetas:', err) }
    navigate(`/admin/etiquetas?ids=${ids.join(',')}`)
  }

  // Cargar por escaneo (estilo Flex): el QR de la etiqueta es https://limpack.uy/d/{token}.
  // El lector USB tipea esa URL + Enter → parseamos el token y llamamos a admin_escanear_cargar.
  async function onEscanear(raw) {
    const s = (raw || '').trim()
    if (!s) return
    const m = s.match(/\/d\/([^/?\s]+)/)
    const token = m ? m[1] : s
    try {
      const { data, error } = await supabase.rpc('admin_escanear_cargar', { p_token: token })
      if (error) throw error
      if (data?.ok) {
        if (data.entrega_id) {
          setEntregas(es => es.map(e => e.id === data.entrega_id ? { ...e, estado: 'en_ruta' } : e))
        }
        setScanFeed(f => [{ ok: true, txt: `✓ ${data.numero_orden || 'entrega'} ${data.ya_estaba ? '(ya estaba a bordo)' : 'a bordo'} · ${data.a_bordo} cargadas` }, ...f].slice(0, 8))
      } else {
        setScanFeed(f => [{ ok: false, txt: `✕ ${data?.motivo || 'No se pudo cargar'}` }, ...f].slice(0, 8))
      }
    } catch (e) {
      setScanFeed(f => [{ ok: false, txt: `✕ ${e.message || e}` }, ...f].slice(0, 8))
    }
  }

  function hojaArmado() {
    if (seleccionadas.size === 0) {
      alert('Seleccioná al menos una entrega para generar la hoja de armado.')
      return
    }
    navigate(`/admin/armado?ids=${[...seleccionadas].join(',')}`)
  }

  async function cambiarBultos(entregaId, nuevo) {
    const n = Math.max(1, Math.min(20, nuevo))
    // optimistic update
    setEntregas(es => es.map(e => e.id === entregaId ? { ...e, bultos_total: n } : e))
    try { await actualizarBultos(entregaId, n) } catch (err) {
      console.error('actualizarBultos:', err)
      load() // revertir si falla
    }
  }

  const corteInfo = useMemo(() => {
    const after = isAfterCutoff()
    const dia = getDiaRepartoLabel()
    if (after) return { tipo: 'err', msg: `🔒 Corte cerrado (${CUTOFF_HOUR}hs) · la ruta de mañana (${dia}) ya quedó fija` }
    const restantes = CUTOFF_HOUR - new Date().getHours()
    return { tipo: 'info', msg: `Faltan ${restantes}h para el corte de las ${CUTOFF_HOUR}hs` }
  }, [fecha])
  // El banner de corte solo tiene sentido mirando hoy/mañana en vista de día.
  const corteVisible = modoRango === 'dia' && (fecha === todayStr() || fecha === tomorrowStr())
  const enRango = modoRango !== 'dia'

  const programadasCount = entregas.filter(e => e.estado === 'programada').length
  const enRutaCount = entregas.filter(e => e.estado === 'en_ruta').length
  // Coherencia es_test: base visible (oculta entregas de prueba por default). Afecta lista + KPIs + conteos.
  const entVis = useMemo(() => ocultarTests ? entregas.filter(e => !e.es_prueba) : entregas, [entregas, ocultarTests])
  const testsOcultos = useMemo(() => entregas.filter(e => e.es_prueba).length, [entregas])

  const entregadasCount = entVis.filter(e => e.estado === 'entregada').length
  const selProgramadasCount = entVis.filter(e => seleccionadas.has(e.id) && e.estado === 'programada').length
  const sinAsignarCount = entVis.filter(e => !e.repartidor_id && e.estado !== 'cancelada').length

  const entregasFiltradas = useMemo(() => {
    let r = entVis
    if (clienteFiltradoId) {
      r = r.filter(e => e.cliente?.id === clienteFiltradoId || e.cliente_suscriptor_id === clienteFiltradoId)
    }
    if (filtroRep === 'sin_asignar') r = r.filter(e => !e.repartidor_id)
    else if (filtroRep !== 'todos') r = r.filter(e => e.repartidor_id === filtroRep)
    // Filtro por estado (chips)
    if (filtroEstado === 'activas') r = r.filter(e => !['cancelada', 'rechazada'].includes(e.estado))
    else if (filtroEstado !== 'todas') r = r.filter(e => e.estado === filtroEstado)
    // Búsqueda por #, cliente, barrio
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase().trim()
      r = r.filter(e => {
        return (e.numero_orden || '').toLowerCase().includes(q)
          || (e.facturacion_razon_social || '').toLowerCase().includes(q)
          || (quienRecibe(e.cliente)?.nombre || '').toLowerCase().includes(q)
          || (e.cliente?.barrio || e.barrio_snapshot || '').toLowerCase().includes(q)
          || (e.cliente?.direccion || e.direccion_snapshot || '').toLowerCase().includes(q)
      })
    }
    return r
  }, [entVis, filtroRep, filtroEstado, busqueda, clienteFiltradoId])

  // Split activas / archivadas para render
  const entregasActivas = entregasFiltradas.filter(e => !['cancelada', 'rechazada'].includes(e.estado))
  const entregasArchivadas = filtroEstado === 'activas'
    ? entVis.filter(e => ['cancelada', 'rechazada'].includes(e.estado))
    : []

  // Agrupar activas por ventana horaria
  const entregasPorVentana = useMemo(() => {
    const grupos = {}
    for (const v of VENTANAS_HORARIAS) grupos[v.key] = []
    for (const e of entregasActivas) {
      const k = e.ventana_horaria || 'manana'
      if (!grupos[k]) grupos[k] = []
      grupos[k].push(e)
    }
    // Sort por barrio dentro de cada ventana
    for (const k of Object.keys(grupos)) {
      grupos[k].sort((a, b) => (a.cliente?.barrio || a.barrio_snapshot || '').localeCompare(b.cliente?.barrio || b.barrio_snapshot || ''))
    }
    return grupos
  }, [entregasActivas])

  // Fix #2 (7-jul-2026): en modo rango, agrupar por fecha antes de por ventana
  const entregasPorFecha = useMemo(() => {
    if (modoRango === 'dia') return null
    const grupos = {}
    for (const e of entregasActivas) {
      const f = e.fecha_programada
      if (!grupos[f]) grupos[f] = []
      grupos[f].push(e)
    }
    return grupos
  }, [entregasActivas, modoRango])

  // Totales para KPIs contextuales
  const bultosTotales = entregasActivas.reduce((s, e) => s + (Number(e.bultos_total) || 1), 0)
  const facturacionPendiente = entregasActivas.reduce((s, e) => s + (Number(e.monto_total) || 0), 0)
  const barriosUnicos = new Set(entregasActivas.map(e => e.cliente?.barrio || e.barrio_snapshot).filter(Boolean))
  const etiquetasImpresas = entregasActivas.filter(e => e.etiqueta_impresa_at).length

  const clienteFiltradoNombre = clienteFiltradoId
    ? (entregasFiltradas[0]?.cliente?.titular?.nombre ||
       entregasFiltradas[0]?.cliente?.alias || 'cliente')
    : null

  function clearClienteFiltro() {
    const next = new URLSearchParams(searchParams)
    next.delete('cliente')
    setSearchParams(next)
  }

  return (
    <AdminLayout>
      <div className="page">
        <h1>Entregas</h1>
        <p className="lead">Lista del día con corte 16hs · Imprimir etiquetas · Salir a ruta</p>

        {/* Barra de estado fina (fusiona corte + ML pendiente en una línea) */}
        {(corteVisible || mlPendientes > 0) && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
            {corteVisible && (
              <span
                title={corteInfo.msg}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
                  padding: '6px 11px', borderRadius: 8,
                  background: corteInfo.tipo === 'err' ? '#FEE2E2' : corteInfo.tipo === 'ok' ? '#DCFCE7' : '#FEF3C7',
                  color: corteInfo.tipo === 'err' ? '#991B1B' : corteInfo.tipo === 'ok' ? '#166534' : '#92400E',
                }}
              >🕐 {corteInfo.msg}</span>
            )}
            {mlPendientes > 0 && (
              <Link to="/admin/canales/mercadolibre/despacho"
                title="Mercado Envíos — canal separado, no se mezcla con la ruta de suscriptores/tienda"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
                  padding: '6px 11px', borderRadius: 8, background: '#FEF3C7', color: '#92400E', textDecoration: 'none',
                }}
              >📦 {mlPendientes} venta{mlPendientes !== 1 ? 's' : ''} ML pendiente{mlPendientes !== 1 ? 's' : ''}
                <span style={{ fontWeight: 800, textDecoration: 'underline' }}>Ir a Despacho →</span>
              </Link>
            )}
          </div>
        )}

        {clienteFiltradoId && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px', marginBottom: 14,
            background: '#E0F7F9', border: '1px solid #00A8B5',
            borderLeft: '4px solid #00A8B5',
            borderRadius: 8, fontSize: 13,
          }}>
            <span style={{ color: '#0E7490', fontWeight: 700 }}>
              🔍 Filtrado por cliente: <span style={{ color: '#1A3461' }}>{clienteFiltradoNombre}</span>
            </span>
            <button onClick={clearClienteFiltro}
              style={{ marginLeft: 'auto', background: '#fff', border: '1px solid #00A8B5',
                color: '#0E7490', padding: '4px 10px', borderRadius: 6,
                fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              × Quitar filtro
            </button>
          </div>
        )}
        {/* Fila de filtros: fecha toggle + chips estado + selector repartidor */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <div style={{ display: 'flex', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, padding: 3 }}>
            <button
              onClick={() => { setModoRango('dia'); setFecha(todayStr()) }}
              style={{ border: 0, background: modoRango === 'dia' && fecha === todayStr() ? '#1A3461' : 'transparent', color: modoRango === 'dia' && fecha === todayStr() ? '#fff' : '#64748B', padding: '6px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', borderRadius: 7 }}
            >Hoy</button>
            <button
              onClick={() => { setModoRango('dia'); setFecha(tomorrowStr()) }}
              style={{ border: 0, background: modoRango === 'dia' && fecha === tomorrowStr() ? '#1A3461' : 'transparent', color: modoRango === 'dia' && fecha === tomorrowStr() ? '#fff' : '#64748B', padding: '6px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', borderRadius: 7 }}
            >Mañana</button>
            <button
              onClick={() => setModoRango('proximos7')}
              style={{ border: 0, background: modoRango === 'proximos7' ? '#1A3461' : 'transparent', color: modoRango === 'proximos7' ? '#fff' : '#64748B', padding: '6px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', borderRadius: 7 }}
              title="Entregas de los próximos 7 días agrupadas por fecha"
            >Próx 7 días</button>
            <button
              onClick={() => setModoRango('semana')}
              style={{ border: 0, background: modoRango === 'semana' ? '#1A3461' : 'transparent', color: modoRango === 'semana' ? '#fff' : '#64748B', padding: '6px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', borderRadius: 7 }}
              title="Semana ISO (lun a dom) que contiene la fecha seleccionada"
            >Semana</button>
            <input
              type="date"
              value={fecha}
              onChange={e => { setFecha(e.target.value); setModoRango('dia') }}
              style={{ border: 0, padding: '6px 10px', fontSize: 12.5, background: 'transparent' }}
            />
          </div>
          <div style={{ width: 1, height: 22, background: '#E2E8F0' }} />
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {[
              { v: 'activas', label: 'Activas', n: entregasActivas.length },
              { v: 'programada', label: 'Programadas', n: programadasCount },
              { v: 'en_ruta', label: 'En ruta', n: enRutaCount },
              { v: 'entregada', label: 'Entregadas', n: entregadasCount },
            ].map(c => (
              <button
                key={c.v}
                onClick={() => setFiltroEstado(c.v)}
                style={{
                  padding: '5px 12px', borderRadius: 999, fontSize: 11.5, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                  background: filtroEstado === c.v ? '#DBEAFE' : '#fff',
                  color: filtroEstado === c.v ? '#1E40AF' : '#64748B',
                  border: filtroEstado === c.v ? '1px solid #1E40AF' : '1px solid #E2E8F0',
                }}
              >{c.label} <span style={{ opacity: 0.7 }}>· {c.n}</span></button>
            ))}
            {testsOcultos > 0 && (
              <button
                onClick={() => setOcultarTests(v => !v)}
                title="Ocultar/mostrar entregas de prueba (es_test / es_prueba)"
                style={{
                  padding: '5px 12px', borderRadius: 999, fontSize: 11.5, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                  background: ocultarTests ? '#00A8B5' : '#fff',
                  color: ocultarTests ? '#fff' : '#64748B',
                  border: ocultarTests ? '1px solid #00A8B5' : '1px solid #E2E8F0',
                }}
              >🙈 {ocultarTests ? `Tests ocultos · ${testsOcultos}` : `Mostrando tests · ${testsOcultos}`}</button>
            )}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 11.5, color: '#64748B' }}>Repartidor:</span>
            <select
              value={filtroRep}
              onChange={e => setFiltroRep(e.target.value)}
              style={{ fontSize: 12.5, padding: '6px 10px', borderRadius: 8, border: '1px solid #CBD5E1', background: '#fff', cursor: 'pointer' }}
            >
              <option value="todos">Todos ({entVis.length})</option>
              <option value="sin_asignar">Sin asignar ({sinAsignarCount})</option>
              {usuarios.map(u => {
                const n = entregas.filter(e => e.repartidor_id === u.id).length
                return <option key={u.id} value={u.id}>{u.nombre} ({n})</option>
              })}
            </select>
          </div>
        </div>

        {/* KPIs contextuales */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #E2E8F0' }}>
            {enRango ? (
              <>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em' }}>Entregas del rango</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#1A3461', marginTop: 3 }}>{entregasActivas.length}</div>
                <div style={{ fontSize: 10.5, color: '#94A3B8', marginTop: 2 }}>capacidad se mide por día</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em' }}>Capacidad ruta</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#16A34A', marginTop: 3 }}>{entregasActivas.length} <span style={{ fontSize: 13, color: '#94A3B8', fontWeight: 500 }}>/ {capacidad}</span></div>
                <div style={{ fontSize: 10.5, color: '#64748B', marginTop: 2 }}>{Math.max(0, capacidad - entregasActivas.length)} cupos disponibles</div>
                <div style={{ height: 4, background: '#F1F5F9', borderRadius: 3, marginTop: 8, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(100, (entregasActivas.length / capacidad) * 100)}%`, background: entregasActivas.length >= capacidad ? '#EF4444' : entregasActivas.length >= capacidad * 0.8 ? '#F59E0B' : '#16A34A' }} />
                </div>
              </>
            )}
          </div>
          <div style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #E2E8F0' }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em' }}>Bultos totales</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1A3461', marginTop: 3 }}>{bultosTotales}</div>
            <div style={{ fontSize: 10.5, color: '#64748B', marginTop: 2 }}>{entregasActivas.length} entrega{entregasActivas.length !== 1 ? 's' : ''}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #E2E8F0' }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em' }}>Facturación</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#00A8B5', marginTop: 3 }}>${Math.round(facturacionPendiente).toLocaleString('es-UY')}</div>
            <div style={{ fontSize: 10.5, color: '#64748B', marginTop: 2 }}>de las activas</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #E2E8F0' }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em' }}>Barrios</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1A3461', marginTop: 3 }}>{barriosUnicos.size}</div>
            <div style={{ fontSize: 10.5, color: '#64748B', marginTop: 2, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{[...barriosUnicos].join(', ') || '—'}</div>
          </div>
        </div>

        <div className="btn-row" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <button className="btn ghost" onClick={toggleAll} title="Seleccionar / deseleccionar todo lo visible">
            ☑︎ {entregasActivas.length > 0 && entregasActivas.every(e => seleccionadas.has(e.id)) ? 'Quitar todo' : 'Seleccionar todo'}
          </button>
          {seleccionadas.size > 0 && (
            <span style={{ fontSize: 12.5, fontWeight: 700, color: '#1E40AF', background: '#DBEAFE', padding: '5px 10px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {seleccionadas.size} seleccionada{seleccionadas.size !== 1 ? 's' : ''}
              <button onClick={limpiarSeleccion} title="Limpiar selección" style={{ border: 0, background: 'transparent', color: '#1E40AF', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
            </span>
          )}
          <button
            className="btn primary"
            onClick={() => setScanOpen(v => !v)}
            title="Escaneá el QR de la etiqueta al cargar cada bulto → pasa a 'en ruta' (estilo Flex)"
          >
            📷 Cargar (escanear){enRutaCount > 0 ? ` · ${enRutaCount} a bordo` : ''}
          </button>
          <button
            className="btn ghost"
            onClick={salirARuta}
            disabled={selProgramadasCount === 0 || enviando}
            title="Fallback manual: marca las programadas seleccionadas como 'en ruta' sin escanear (por si falla el lector)"
          >
            🚐 En ruta a mano ({selProgramadasCount})
          </button>
          <button className="btn ghost" onClick={hojaArmado} disabled={seleccionadas.size === 0}>
            📋 Hoja de armado ({seleccionadas.size})
          </button>
          <button className="btn ghost" onClick={imprimirEtiquetas} disabled={seleccionadas.size === 0}>
            🖨️ Imprimir etiquetas ({seleccionadas.size})
          </button>
          {entregasActivas.length > 0 && (
            <span
              title="Etiquetas impresas sobre entregas activas del día"
              style={{ fontSize: 12, fontWeight: 700, padding: '5px 10px', borderRadius: 999,
                color: etiquetasImpresas >= entregasActivas.length ? '#166534' : '#64748B',
                background: etiquetasImpresas >= entregasActivas.length ? '#DCFCE7' : '#F1F5F9' }}
            >🏷 {etiquetasImpresas}/{entregasActivas.length} impresas</span>
          )}
          {seleccionadas.size > 0 && usuarios.length > 0 && (
            <select
              className="date-input"
              style={{ padding: '6px 10px', fontSize: 13 }}
              defaultValue=""
              onChange={e => {
                const v = e.target.value
                if (!v) return
                asignarSeleccionadasA(v === '__null__' ? null : v)
                e.target.value = ''
              }}
              title="Asignar a repartidor en bloque"
            >
              <option value="">👤 Asignar a… ({seleccionadas.size})</option>
              <option value="__null__">— Quitar asignación —</option>
              {usuarios.map(u => (
                <option key={u.id} value={u.id}>{u.nombre} {u.role === 'admin' ? '(admin)' : ''}</option>
              ))}
            </select>
          )}
          <button className="btn ghost" onClick={load}>↺ Recargar</button>
        </div>

        {scanOpen && (
          <ScanCargarPanel onToken={onEscanear} feed={scanFeed} aBordo={enRutaCount} onClose={() => { setScanOpen(false); setScanFeed([]) }} />
        )}

        {/* Barra búsqueda */}
        <div style={{ display: 'flex', gap: 6, marginTop: 12, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="search"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder="🔍 Buscar por # / cliente / barrio / dirección"
            style={{ flex: 1, minWidth: 200, padding: '7px 12px', borderRadius: 8, border: '1px solid #CBD5E1', fontSize: 12.5, background: '#fff', fontFamily: 'inherit' }}
          />
          {busqueda && (
            <button onClick={() => setBusqueda('')} style={{ background: 'transparent', border: '1px solid #CBD5E1', borderRadius: 8, padding: '5px 10px', fontSize: 11, color: '#64748B', cursor: 'pointer', fontFamily: 'inherit' }}>× Limpiar</button>
          )}
        </div>

        {loading ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--muted)' }}>Cargando...</div>
        ) : entregasActivas.length === 0 && entregasArchivadas.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--muted)', padding: 30 }}>
            🌱 No hay entregas para esta fecha con los filtros actuales.
          </div>
        ) : (
          <>
            {modoRango !== 'dia' && entregasPorFecha ? (
              // Vista rango: agrupar por fecha, después por ventana
              Object.keys(entregasPorFecha).sort().map(f => {
                const itemsFecha = entregasPorFecha[f]
                const porVentana = { manana: [], tarde: [] }
                for (const e of itemsFecha) {
                  const k = e.ventana_horaria || 'manana'
                  if (!porVentana[k]) porVentana[k] = []
                  porVentana[k].push(e)
                }
                const fechaLbl = new Date(f + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'short' })
                return (
                  <div key={f} style={{ marginBottom: 22, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 12, padding: 12 }}>
                    <div style={{ fontSize: 13, color: '#1A3461', fontWeight: 800, textTransform: 'capitalize', margin: '2px 4px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      📅 {fechaLbl}
                      <span style={{ color: '#94A3B8', fontWeight: 400 }}>· {itemsFecha.length} entrega{itemsFecha.length !== 1 ? 's' : ''}</span>
                    </div>
                    {[...VENTANAS_HORARIAS, ...VENTANAS_SISTEMA].map(v => {
                      const items = porVentana[v.key] || []
                      if (items.length === 0) return null
                      return (
                        <div key={v.key} style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700, margin: '8px 4px 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 13 }}>{v.emoji}</span>
                            {v.label} · {v.hi}–{v.hf}hs
                            <span style={{ color: '#94A3B8', fontWeight: 400, textTransform: 'none' }}>· {items.length}</span>
                          </div>
                          {items.map(e => <EntregaCard key={e.id} e={e} usuarios={usuarios} seleccionadas={seleccionadas} toggle={toggle} cambiarBultos={cambiarBultos} cambiarAsignado={cambiarAsignado} onArmar={() => setArmandoEntrega(e)} onEditarKit={() => setEditandoKit(e)} load={load} />)}
                        </div>
                      )
                    })}
                  </div>
                )
              })
            ) : (
              // Vista día única: agrupar solo por ventana
              [...VENTANAS_HORARIAS, ...VENTANAS_SISTEMA].map(v => {
                const items = entregasPorVentana[v.key] || []
                if (items.length === 0) return null
                return (
                  <div key={v.key} style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 11.5, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700, margin: '14px 0 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 15 }}>{v.emoji}</span>
                      {v.label} · {v.hi}–{v.hf}hs
                      <span style={{ color: '#94A3B8', fontWeight: 400, textTransform: 'none' }}>· {items.length} entrega{items.length !== 1 ? 's' : ''}</span>
                    </div>
                    {items.map(e => <EntregaCard key={e.id} e={e} usuarios={usuarios} seleccionadas={seleccionadas} toggle={toggle} cambiarBultos={cambiarBultos} cambiarAsignado={cambiarAsignado} onArmar={() => setArmandoEntrega(e)} onEditarKit={() => setEditandoKit(e)} load={load} />)}
                  </div>
                )
              })
            )}

            {entregasArchivadas.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <div style={{ fontSize: 11.5, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700, margin: '14px 0 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 15 }}>🗄</span>
                  Archivadas <span style={{ color: '#94A3B8', fontWeight: 400, textTransform: 'none' }}>· {entregasArchivadas.length} cancelada{entregasArchivadas.length !== 1 ? 's' : ''}</span>
                  <button onClick={() => setMostrarArchivadas(!mostrarArchivadas)} style={{ marginLeft: 'auto', background: 'transparent', border: 0, color: '#0369A1', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {mostrarArchivadas ? 'Ocultar' : 'Mostrar'}
                  </button>
                </div>
                {mostrarArchivadas && entregasArchivadas.map(e => (
                  <EntregaCard key={e.id} e={e} usuarios={usuarios} seleccionadas={seleccionadas} toggle={toggle} cambiarBultos={cambiarBultos} cambiarAsignado={cambiarAsignado} onArmar={() => setArmandoEntrega(e)} onEditarKit={() => setEditandoKit(e)} load={load} archivada />
                ))}
              </div>
            )}
          </>
        )}

        {armandoEntrega && (
          <ArmarModal
            entrega={armandoEntrega}
            onClose={() => setArmandoEntrega(null)}
            onDone={() => { setArmandoEntrega(null); load() }}
          />
        )}

        {editandoKit && (
          <EditarKitModal
            entrega={editandoKit}
            onClose={() => setEditandoKit(null)}
            onDone={() => { setEditandoKit(null); load() }}
          />
        )}
      </div>
    </AdminLayout>
  )
}

/* Card por entrega — reemplazo del <tr> viejo */
/* Panel de carga por escaneo (etapa 3): lector USB / tecleo del código. Cámara → app repartidor (etapa 4). */
function ScanCargarPanel({ onToken, feed, aBordo, onClose }) {
  const [val, setVal] = useState('')
  const inputRef = useRef(null)
  useEffect(() => { inputRef.current?.focus() }, [])
  function submit() {
    const v = val
    setVal('')
    if (v.trim()) onToken(v)
    inputRef.current?.focus()
  }
  return (
    <div style={{ background: '#0c1a30', color: '#fff', borderRadius: 12, padding: '14px 16px', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 20 }}>📷</span>
        <div style={{ fontWeight: 800, fontSize: 14 }}>Cargar por escaneo</div>
        <span style={{ fontSize: 12, color: '#5BE7C0', fontWeight: 800 }}>{aBordo} a bordo</span>
        <button onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid rgba(255,255,255,.25)', color: '#fff', borderRadius: 8, padding: '5px 10px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Cerrar</button>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          ref={inputRef}
          value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit() } }}
          placeholder="Escaneá el QR de la etiqueta (o pegá el código) y Enter…"
          style={{ flex: 1, minWidth: 200, padding: '10px 12px', borderRadius: 8, border: 0, fontSize: 14, fontFamily: 'inherit' }}
        />
        <button onClick={submit} style={{ background: '#00A8B5', color: '#fff', border: 0, borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>Cargar</button>
      </div>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)', marginTop: 8 }}>
        Lector USB o teclado: escaneá y se carga solo. El QR de la etiqueta pasa la entrega a “en ruta”. (Cámara del celular → app del repartidor, próximamente.)
      </div>
      {feed.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {feed.map((f, i) => (
            <div key={i} style={{ fontSize: 12.5, fontWeight: 700, color: f.ok ? '#5BE7C0' : '#FCA5A5' }}>{f.txt}</div>
          ))}
        </div>
      )}
    </div>
  )
}

function EntregaCard({ e, usuarios, seleccionadas, toggle, cambiarBultos, cambiarAsignado, onArmar, onEditarKit, load, archivada }) {
  const navigate = useNavigate()
  // Etiqueta con estado por envío: al imprimir se marca impresa (RPC) y el botón pasa a "Reimprimir".
  async function imprimirEtiqueta() {
    try { await supabase.rpc('admin_marcar_etiqueta_impresa', { p_ids: [e.id] }) } catch (err) { console.error('marcar etiqueta:', err) }
    navigate(`/admin/etiquetas?ids=${e.id}`)
  }
  const tieneExtras = (e.productos_extras || []).length > 0
  const c = ESTADO_COLOR[e.estado] || ESTADO_COLOR.programada
  const esB2B = e.facturacion_tipo === 'empresa' || e.cliente?.__esB2B
  const esSueltos = !e.kit_codigo && tieneExtras
  // Kit tocado a mano en esta entrega (sustitución/quita/regalo/agregado)
  const kitEditado = !!e.kit_codigo && (e.productos_extras || []).some(
    x => (Number(x?.cantidad) || 0) < 0 || ['quita', 'sustitucion', 'regalo', 'mas_cantidad', 'agregado'].includes(x?.origen)
  )
  const rec = quienRecibe(e.cliente)
  const fantasia = e.nombre_fantasia_snapshot || e.cliente?.__fantasia
  const razonSocial = e.facturacion_razon_social || e.cliente?.__razon_social
  const nombre = fantasia || razonSocial || rec?.nombre || '—'
  const telefono = rec?.phone
  const barrio = e.cliente?.barrio || e.barrio_snapshot || ''
  const direccion = e.cliente?.direccion || e.direccion_snapshot || ''
  const apto = e.cliente?.apto || e.apto_snapshot
  const noPagado = esB2B && !e.pago_id && e.estado === 'programada' // B2B contra entrega
  // Fix #25 (7-jul-2026): armar solo si (a) estado programada/en_armado, (b) ya pasó corte D-1 16hs, (c) tiene pago vinculado.
  // Antes del corte D-1 el cron no ejecutó el cobro → riesgo de empacar sin cobrar.
  // 16-jul-2026: Las 3 condiciones aplican SOLO a B2C. En B2B se cobra post-entrega
  // (cuenta corriente / e-Factura crédito), entonces no hay cron D-1 esperando y
  // el pago_id no existe hasta después de facturar. Para B2B alcanza con estado válido.
  const cortePaso = yaPasoCorteD1(e.fecha_programada)
  const tienePago = !!e.pago_id  // el cron D-1 linkea pago_id al cobrar exitosamente
  const puedeArmar = ['programada', 'en_armado'].includes(e.estado) && (
    esB2B || (cortePaso && tienePago)
  )
  const razonNoPuedeArmar = esB2B
    ? null
    : !cortePaso
      ? 'Esperar corte D-1 16hs'
      : !tienePago
        ? '⚠ Pago no confirmado — resolver antes de armar'
        : null

  return (
    <div style={{
      background: archivada ? '#F1F5F9' : '#fff',
      opacity: archivada ? 0.7 : 1,
      border: '1px solid #E2E8F0',
      borderLeft: `4px solid ${archivada ? '#CBD5E1' : (c.dot || '#CBD5E1')}`,
      borderRadius: 12,
      padding: '12px 14px',
      marginBottom: 8,
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr) auto auto',
      gap: 14,
      alignItems: 'center',
    }}>
      <input
        type="checkbox"
        checked={seleccionadas.has(e.id)}
        onChange={() => toggle(e.id)}
        disabled={archivada}
        style={{ width: 16, height: 16, cursor: archivada ? 'default' : 'pointer' }}
      />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#64748B' }}>{e.numero_orden}</span>
          {!archivada && <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: c.bg, color: c.text, textTransform: 'uppercase', letterSpacing: '.02em' }}>{ESTADO_LABEL[e.estado]}</span>}
          {esB2B && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: '#DBEAFE', color: '#1E40AF' }}>B2B</span>}
          {!esB2B && e.cliente && !archivada && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: '#EDE9FE', color: '#5B21B6' }}>B2C</span>}
          {esSueltos && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: '#FEF3C7', color: '#78350F' }}>🛍 {(e.productos_extras || []).length} sueltos</span>}
          {e.kit?.nombre && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: '#D1FAE5', color: '#065F46' }}>{e.kit.nombre}{!kitEditado && tieneExtras ? ' + extras' : ''}</span>}
          {kitEditado && <span title="Kit modificado en esta entrega (sustitución / quita / regalo)" style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: '#EDE9FE', color: '#5B21B6' }}>✏️ kit editado</span>}
          {noPagado && <span title="B2B: se factura y cobra después de entregar" style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: '#FEF3C7', color: '#78350F' }}>💳 Cobro post-entrega</span>}
          {archivada && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: c.bg, color: c.text, textTransform: 'uppercase' }}>{ESTADO_LABEL[e.estado]}</span>}
          {e.monto_total && <span style={{ marginLeft: 'auto', fontSize: 11.5, color: '#64748B', fontWeight: 600 }}>${Math.round(Number(e.monto_total)).toLocaleString('es-UY')}</span>}
        </div>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#1A3461', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nombre}</div>
        {fantasia && razonSocial && fantasia !== razonSocial && (
          <div style={{ fontSize: 11, color: '#94A3B8', fontStyle: 'italic', marginTop: 1 }}>Razón social: {razonSocial}</div>
        )}
        <div style={{ fontSize: 12.5, color: '#64748B', marginTop: 2 }}>
          📍 <b style={{ color: '#334155' }}>{barrio || '—'}</b> · {direccion || '—'}{apto ? ` · ${apto}` : ''}
          {telefono && <span style={{ color: '#94A3B8', marginLeft: 6 }}>· 📱 {formatearTelUY(telefono)}</span>}
          {e.km_recorridos != null && (
            <span
              style={{ color: '#1a3461', marginLeft: 6, fontWeight: 700 }}
              title={`${Number(e.km_recorridos).toFixed(2)} km hasta acá (origen: ${e.km_origen || 'desconocido'})${e.km_regreso_origen != null ? ` + ${Number(e.km_regreso_origen).toFixed(2)} km de regreso al origen (última entrega del día)` : ''}`}
            >
              · 🚗 {Number(e.km_recorridos).toFixed(1)}
              {e.km_regreso_origen != null && <span style={{ color: '#64748b', fontWeight: 500 }}> +{Number(e.km_regreso_origen).toFixed(1)}</span>}
              {' km'}
            </span>
          )}
        </div>
        {esRegalo(e.cliente) && (
          <div style={{ fontSize: 11, color: '#7C3AED', marginTop: 3, fontWeight: 700 }}>🎁 paga: {quienPaga(e.cliente)?.nombre}</div>
        )}
        {e.estado === 'no_entregada' && <NoEntregaCell entrega={e} onReagendado={load} />}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button
          onClick={() => cambiarBultos(e.id, (e.bultos_total || 1) - 1)}
          disabled={(e.bultos_total || 1) <= 1 || archivada}
          style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid #E2E8F0', background: '#F1F5F9', cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit' }}
        >−</button>
        <span style={{ fontWeight: 700, minWidth: 18, textAlign: 'center', fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>{e.bultos_total || 1}</span>
        <button
          onClick={() => cambiarBultos(e.id, (e.bultos_total || 1) + 1)}
          disabled={archivada}
          style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid #E2E8F0', background: '#F1F5F9', cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit' }}
        >+</button>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {!archivada && (
          <select
            value={e.repartidor_id || ''}
            onChange={ev => cambiarAsignado(e.id, ev.target.value || null)}
            disabled={e.estado === 'entregada'}
            style={{
              padding: '5px 8px', fontSize: 11,
              borderRadius: 6,
              border: e.repartidor_id ? '1px solid #CBD5E1' : '1.5px solid #F59E0B',
              background: e.repartidor_id ? '#fff' : '#FEF3C7',
              fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', maxWidth: 110,
            }}
          >
            <option value="">— sin asignar —</option>
            {usuarios.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
          </select>
        )}
        {e.kit_codigo && ['programada', 'en_armado'].includes(e.estado) && !archivada && (
          <button
            onClick={onEditarKit}
            title="Editar kit de esta entrega: sustituir, quitar o agregar productos y regalos"
            style={{ background: '#EDE9FE', color: '#5B21B6', border: 0, borderRadius: 8, padding: '6px 10px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
          >✏️ Kit</button>
        )}
        {['programada', 'en_armado'].includes(e.estado) && !archivada && (
          puedeArmar ? (
            <button
              onClick={() => {
                // Warning si es B2B y la fecha_programada es futura — para evitar armar/entregar
                // por accidente un pedido que era para mañana o después.
                if (esB2B && e.fecha_programada) {
                  const hoy = new Date(); hoy.setHours(0, 0, 0, 0)
                  const fp = new Date(e.fecha_programada + 'T00:00:00')
                  if (fp.getTime() > hoy.getTime()) {
                    const dias = Math.round((fp.getTime() - hoy.getTime()) / 86400000)
                    const dStr = fp.toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' })
                    if (!confirm(`⚠ Esta entrega es para ${dStr} (dentro de ${dias} día${dias === 1 ? '' : 's'}).\n\n¿Seguro que la vas a armar hoy?`)) return
                  }
                }
                onArmar()
              }}
              style={{ background: '#DBEAFE', color: '#1E40AF', border: 0, borderRadius: 8, padding: '6px 12px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
              title="Armar el pedido con checklist"
            >📦 Armar →</button>
          ) : (
            <button
              disabled
              style={{ background: '#F1F5F9', color: '#94A3B8', border: 0, borderRadius: 8, padding: '6px 12px', fontSize: 11.5, fontWeight: 700, cursor: 'not-allowed', fontFamily: 'inherit' }}
              title={razonNoPuedeArmar || 'No se puede armar aún'}
            >🔒 Armar</button>
          )
        )}
        {!archivada && !['cancelada', 'cancelada_pago_fallido', 'cancelada_por_pausa', 'pendiente_pago'].includes(e.estado) && (
          <button
            onClick={imprimirEtiqueta}
            title={e.etiqueta_impresa_at ? `Etiqueta ya impresa (${new Date(e.etiqueta_impresa_at).toLocaleString('es-UY')}) — reimprimir` : 'Imprimir etiqueta con QR'}
            style={{ background: e.etiqueta_impresa_at ? '#fff' : '#1A3461', color: e.etiqueta_impresa_at ? '#64748B' : '#fff', border: e.etiqueta_impresa_at ? '1px solid #E2E8F0' : 0, borderRadius: 8, padding: '6px 10px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}
          >🖨 {e.etiqueta_impresa_at ? 'Reimprimir' : 'Imprimir'}{e.etiqueta_impresa_at && <span style={{ color: '#16A34A', fontWeight: 800 }}>✓</span>}</button>
        )}
        {e.estado === 'entregada' && e.foto_url && <VerFotoBtn path={e.foto_url} />}
      </div>
    </div>
  )
}

/* Modal de armado con checklist de items */
function ArmarModal({ entrega, onClose, onDone }) {
  const [items, setItems] = useState([])
  const [tildados, setTildados] = useState(new Set())
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)

  useEffect(() => {
    (async () => {
      setCargando(true)
      // Traer items del pedido asociado
      const { data: pedido } = await supabase
        .from('pedidos')
        .select('items')
        .eq('numero_orden', entrega.numero_orden)
        .maybeSingle()
      const rawItems = pedido?.items || entrega.productos_extras || []
      setItems(rawItems)
      setCargando(false)
    })()
  }, [entrega.numero_orden])

  function toggleItem(i) {
    const s = new Set(tildados)
    s.has(i) ? s.delete(i) : s.add(i)
    setTildados(s)
  }

  async function marcarArmado() {
    setGuardando(true)
    // Setear estado del pedido a 'en_armado' si estaba en programada
    // (la entrega no cambia de estado hasta salir a ruta)
    try {
      await supabase.from('pedidos').update({}).eq('numero_orden', entrega.numero_orden)
    } catch {}
    setGuardando(false)
    onDone()
  }

  const catalogo = items.filter(x => x.producto_id || x.sku)
  const aConseguir = items.filter(x => !x.producto_id && !x.sku)

  const total = items.length
  const done = tildados.size
  const listo = done === total && total > 0

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={ev => ev.stopPropagation()} style={{ background: '#fff', borderRadius: 14, maxWidth: 640, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 40px rgba(15,23,42,.3)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, color: '#1A3461', fontWeight: 800 }}>📦 Armar {entrega.numero_orden}</h3>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>Tildá cada producto cuando lo pongas en la caja</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, cursor: 'pointer', color: '#64748B', fontSize: 20, padding: '4px 8px' }}>✕</button>
        </div>
        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
          <div style={{ background: '#F1F5F9', padding: '10px 14px', borderRadius: 8, fontSize: 12.5, marginBottom: 14 }}>
            <strong style={{ color: '#1A3461', fontWeight: 800 }}>{entrega.facturacion_razon_social || quienRecibe(entrega.cliente)?.nombre || '—'}</strong><br />
            {(entrega.direccion_snapshot || entrega.cliente?.direccion) || '—'} · {entrega.barrio_snapshot || entrega.cliente?.barrio || ''}
          </div>

          {cargando ? <div style={{ color: '#64748B' }}>Cargando items...</div> : (
            <>
              {catalogo.length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700, margin: '12px 0 6px' }}>
                    Del catálogo ({catalogo.length})
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {catalogo.map((it, i) => (
                      <li key={i}
                        onClick={() => toggleItem(`c-${i}`)}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px',
                          borderRadius: 8, marginBottom: 4, border: '1px solid #E2E8F0',
                          cursor: 'pointer',
                          background: tildados.has(`c-${i}`) ? '#F0FDF4' : '#fff',
                          borderColor: tildados.has(`c-${i}`) ? '#86EFAC' : '#E2E8F0',
                          opacity: tildados.has(`c-${i}`) ? 0.7 : 1,
                        }}>
                        <input type="checkbox" checked={tildados.has(`c-${i}`)} onChange={() => {}} style={{ width: 18, height: 18, cursor: 'pointer', marginTop: 2 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 13.5, color: '#1A3461', textDecoration: tildados.has(`c-${i}`) ? 'line-through' : 'none' }}>
                            {it.descripcion || it.nombre || '—'}
                          </div>
                          {it.precio_unitario && (
                            <div style={{ fontSize: 11.5, color: '#64748B', marginTop: 2 }}>${Number(it.precio_unitario).toLocaleString('es-UY')} c/u</div>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748B', fontWeight: 700, padding: '3px 8px', background: '#F1F5F9', borderRadius: 6 }}>×{it.cantidad || 1}</div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {aConseguir.length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700, margin: '16px 0 6px' }}>
                    Fuera de catálogo — hay que conseguir ({aConseguir.length})
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {aConseguir.map((it, i) => (
                      <li key={i}
                        onClick={() => toggleItem(`x-${i}`)}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px',
                          borderRadius: 8, marginBottom: 4, border: '1px solid #FCD34D',
                          cursor: 'pointer',
                          background: tildados.has(`x-${i}`) ? '#F0FDF4' : '#FFFBEB',
                          opacity: tildados.has(`x-${i}`) ? 0.7 : 1,
                        }}>
                        <input type="checkbox" checked={tildados.has(`x-${i}`)} onChange={() => {}} style={{ width: 18, height: 18, cursor: 'pointer', marginTop: 2 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 13.5, color: '#1A3461', textDecoration: tildados.has(`x-${i}`) ? 'line-through' : 'none' }}>
                            {it.descripcion || it.nombre || '—'}
                            <span style={{ marginLeft: 6, fontSize: 10, background: '#FEF3C7', color: '#78350F', padding: '1px 6px', borderRadius: 999, fontWeight: 700 }}>🔍 A CONSEGUIR</span>
                          </div>
                          {it.precio_unitario && (
                            <div style={{ fontSize: 11.5, color: '#64748B', marginTop: 2 }}>${Number(it.precio_unitario).toLocaleString('es-UY')} c/u</div>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748B', fontWeight: 700, padding: '3px 8px', background: '#fff', borderRadius: 6 }}>×{it.cantidad || 1}</div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
        <div style={{ padding: '14px 20px', borderTop: '1px solid #E2E8F0', background: '#F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 12, color: '#64748B' }}>
            Progreso: <strong style={{ color: '#1A3461', fontWeight: 800 }}>{done} / {total} productos</strong>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ background: 'transparent', color: '#475569', border: '1.5px solid #E2E8F0', borderRadius: 8, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Cerrar</button>
            <button
              onClick={marcarArmado}
              disabled={!listo || guardando}
              style={{ background: listo ? '#00A8B5' : '#CBD5E1', color: '#fff', border: 0, borderRadius: 8, padding: '7px 14px', fontSize: 12.5, fontWeight: 700, cursor: listo ? 'pointer' : 'not-allowed', fontFamily: 'inherit', opacity: listo ? 1 : 0.5 }}
            >
              {guardando ? 'Guardando...' : '📦 Marcar armado'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* Editor de kit por entrega: sustituir / quitar / agregar productos + regalos.
 * Escribe entregas.productos_extras (deltas, cantidad negativa = quita) y
 * audita en entrega_kit_cambios. NO toca triggers: el stock ya lee extras. */
function EditarKitModal({ entrega, onClose, onDone }) {
  const [template, setTemplate] = useState([])
  const [rows, setRows] = useState([])
  const [catalogo, setCatalogo] = useState([])
  const [historial, setHistorial] = useState([])
  const [motivo, setMotivo] = useState('')
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [pickerFor, setPickerFor] = useState(undefined) // undefined=cerrado, null=agregar, número=sustituir fila
  const [pickerQ, setPickerQ] = useState('')

  const fmt = n => '$' + Math.round(Number(n) || 0).toLocaleString('es-UY')

  useEffect(() => {
    (async () => {
      setCargando(true)
      const [tpl, cat, hist] = await Promise.all([
        getKitTemplateItems(entrega.kit_codigo),
        getProductosCatalogo(),
        getHistorialKitEntrega(entrega.id),
      ])
      setTemplate(tpl)
      setCatalogo(cat)
      setHistorial(hist)
      // Construir filas = caja efectiva (template + deltas actuales)
      const catBySku = Object.fromEntries(cat.map(p => [p.sku, p]))
      const catById = Object.fromEntries(cat.map(p => [p.id, p]))
      const map = {}
      tpl.forEach(t => {
        map[t.producto_id] = {
          producto_id: t.producto_id, sku: t.sku, nombre: t.nombre, marca: t.marca,
          presentacion: t.presentacion, precio_publico: t.precio_publico, iva_pct: t.iva_pct,
          cantidad: t.cantidad, es_regalo: t.es_regalo, cobrado: false, _base: 'kit',
        }
      })
      ;(entrega.productos_extras || []).forEach(x => {
        const pid = x.producto_id || catBySku[x.sku]?.id
        if (!pid) return
        const cat1 = catById[pid] || catBySku[x.sku] || {}
        const q = Number(x.cantidad ?? x.qty ?? 1) || 0
        if (map[pid]) {
          map[pid].cantidad += q
          if (x.es_regalo) map[pid].es_regalo = true
        } else {
          map[pid] = {
            producto_id: pid, sku: cat1.sku || x.sku, nombre: cat1.nombre || x.nombre || 'Producto',
            marca: cat1.marca, presentacion: cat1.presentacion, precio_publico: Number(cat1.precio_publico) || 0,
            iva_pct: cat1.iva_pct, cantidad: q, es_regalo: !!x.es_regalo, cobrado: false, _base: 'nuevo',
          }
        }
      })
      setRows(Object.values(map).filter(r => (Number(r.cantidad) || 0) > 0))
      setCargando(false)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entrega.id])

  const dispBySku = useMemo(() => Object.fromEntries(catalogo.map(p => [p.id, p.disponible])), [catalogo])

  function setQty(i, q) {
    setRows(rs => rs.map((r, idx) => idx === i ? { ...r, cantidad: Math.max(0, q) } : r).filter((r, idx) => !(idx === i && q <= 0)))
  }
  function quitar(i) { setRows(rs => rs.filter((_, idx) => idx !== i)) }
  function toggleModo(i, modo) {
    // modo: 'regalo' | 'cobrar'  (solo filas nuevas)
    setRows(rs => rs.map((r, idx) => idx === i ? { ...r, es_regalo: modo === 'regalo', cobrado: modo === 'cobrar' } : r))
  }
  function elegirProducto(p) {
    if (pickerFor === null) {
      // Agregar nuevo (default regalo)
      setRows(rs => {
        const ya = rs.findIndex(r => r.producto_id === p.id)
        if (ya >= 0) return rs.map((r, idx) => idx === ya ? { ...r, cantidad: (Number(r.cantidad) || 0) + 1 } : r)
        return [...rs, { producto_id: p.id, sku: p.sku, nombre: p.nombre, marca: p.marca, presentacion: p.presentacion, precio_publico: Number(p.precio_publico) || 0, iva_pct: p.iva_pct, cantidad: 1, es_regalo: true, cobrado: false, _base: 'nuevo' }]
      })
    } else if (typeof pickerFor === 'number') {
      // Sustituir la fila pickerFor por p, manteniendo cantidad (sin cobrar, sin regalo)
      setRows(rs => rs.map((r, idx) => idx === pickerFor
        ? { producto_id: p.id, sku: p.sku, nombre: p.nombre, marca: p.marca, presentacion: p.presentacion, precio_publico: Number(p.precio_publico) || 0, iva_pct: p.iva_pct, cantidad: r.cantidad, es_regalo: false, cobrado: false, _base: 'nuevo' }
        : r))
    }
    setPickerFor(undefined); setPickerQ('')
  }

  const finalItems = rows.map(r => ({ producto_id: r.producto_id, sku: r.sku, nombre: r.nombre, cantidad: Number(r.cantidad) || 0, es_regalo: !!r.es_regalo, cobrado: !!r.cobrado, precio_publico: Number(r.precio_publico) || 0 }))
  const diff = useMemo(() => calcularExtrasDesdeEdicion(template, finalItems), [template, rows])
  const montoActual = Number(entrega.monto_total) || 0
  const montoNuevo = montoActual + (diff.montoDelta || 0)
  const hayCambios = (diff.productosExtras || []).length > 0

  async function guardar() {
    setGuardando(true)
    try {
      await guardarEdicionKit({
        entregaId: entrega.id,
        productosExtras: diff.productosExtras,
        montoTotalNuevo: diff.montoDelta !== 0 ? montoNuevo : null,
        cambios: diff.cambios,
        motivo,
      })
      onDone()
    } catch (err) {
      alert('Error al guardar: ' + (err.message || err))
      setGuardando(false)
    }
  }

  const catFiltrado = useMemo(() => {
    const q = pickerQ.toLowerCase().trim()
    let r = catalogo
    if (q) r = r.filter(p => (p.nombre || '').toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q) || (p.marca || '').toLowerCase().includes(q))
    return r.slice(0, 50)
  }, [catalogo, pickerQ])

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={ev => ev.stopPropagation()} style={{ background: '#fff', borderRadius: 14, maxWidth: 720, width: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 40px rgba(15,23,42,.3)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, color: '#1A3461', fontWeight: 800 }}>✏️ Editar kit · {entrega.numero_orden}</h3>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>
              {entrega.kit?.nombre || entrega.kit_codigo} · {entrega.facturacion_razon_social || quienRecibe(entrega.cliente)?.nombre || '—'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, cursor: 'pointer', color: '#64748B', fontSize: 20, padding: '4px 8px' }}>✕</button>
        </div>

        <div style={{ padding: '14px 20px', overflowY: 'auto', flex: 1 }}>
          {cargando ? <div style={{ color: '#64748B' }}>Cargando kit…</div> : pickerFor !== undefined ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <button onClick={() => { setPickerFor(undefined); setPickerQ('') }} style={{ background: 'transparent', border: '1px solid #E2E8F0', borderRadius: 8, padding: '5px 10px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', color: '#475569' }}>← Volver</button>
                <strong style={{ fontSize: 13, color: '#1A3461' }}>{pickerFor === null ? 'Agregar producto' : 'Sustituir por…'}</strong>
              </div>
              <input autoFocus type="search" value={pickerQ} onChange={ev => setPickerQ(ev.target.value)} placeholder="🔍 Buscar por nombre / SKU / marca"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #CBD5E1', fontSize: 13, marginBottom: 10, fontFamily: 'inherit' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {catFiltrado.map(p => (
                  <button key={p.id} onClick={() => elegirProducto(p)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', background: '#fff', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#1A3461' }}>{p.nombre}{p.presentacion ? ` · ${p.presentacion}` : ''}</div>
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>{p.marca || ''} · {fmt(p.precio_publico)} · disp {p.disponible}</div>
                    </div>
                    <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#CBD5E1' }}>{p.sku}</span>
                  </button>
                ))}
                {catFiltrado.length === 0 && <div style={{ color: '#94A3B8', fontSize: 12, padding: 8 }}>Sin resultados.</div>}
              </div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700, marginBottom: 8 }}>
                Caja de esta entrega ({rows.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {rows.map((r, i) => {
                  const disp = dispBySku[r.producto_id]
                  return (
                    <div key={r.producto_id + '-' + i} style={{ border: '1px solid #E2E8F0', borderRadius: 10, padding: '8px 10px', background: r.es_regalo ? '#FFFBEB' : '#fff' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: '#1A3461' }}>
                            {r.es_regalo && '🎁 '}{r.nombre}{r.presentacion ? <span style={{ color: '#64748B', fontWeight: 500 }}> · {r.presentacion}</span> : null}
                          </div>
                          <div style={{ fontSize: 11, color: '#94A3B8' }}>
                            {r.sku} · {fmt(r.precio_publico)}{disp != null ? ` · disp ${disp}` : ''}
                            {r._base === 'kit' ? ' · del kit' : ' · agregado'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <button onClick={() => setQty(i, (Number(r.cantidad) || 0) - 1)} style={{ width: 24, height: 24, borderRadius: 5, border: '1px solid #E2E8F0', background: '#F8FAFC', cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit' }}>−</button>
                          <span style={{ minWidth: 20, textAlign: 'center', fontWeight: 700, fontSize: 13 }}>{r.cantidad}</span>
                          <button onClick={() => setQty(i, (Number(r.cantidad) || 0) + 1)} style={{ width: 24, height: 24, borderRadius: 5, border: '1px solid #E2E8F0', background: '#F8FAFC', cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit' }}>+</button>
                        </div>
                        <button onClick={() => { setPickerFor(i); setPickerQ('') }} title="Sustituir por otro producto" style={{ background: '#EFF6FF', color: '#1E40AF', border: 0, borderRadius: 6, padding: '5px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>⇄</button>
                        <button onClick={() => quitar(i)} title="Quitar del kit" style={{ background: '#FEF2F2', color: '#B91C1C', border: 0, borderRadius: 6, padding: '5px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>✕</button>
                      </div>
                      {r._base === 'nuevo' && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                          <button onClick={() => toggleModo(i, 'regalo')} style={{ flex: 1, padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: r.es_regalo ? '1.5px solid #F59E0B' : '1px solid #E2E8F0', background: r.es_regalo ? '#FEF3C7' : '#fff', color: r.es_regalo ? '#92400E' : '#64748B' }}>🎁 Regalo ($0)</button>
                          <button onClick={() => toggleModo(i, 'cobrar')} style={{ flex: 1, padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: r.cobrado ? '1.5px solid #00A8B5' : '1px solid #E2E8F0', background: r.cobrado ? '#E0F7F9' : '#fff', color: r.cobrado ? '#0E7490' : '#64748B' }}>💲 Cobrar (+{fmt(Math.ceil(r.precio_publico * r.cantidad))})</button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              <button onClick={() => { setPickerFor(null); setPickerQ('') }} style={{ marginTop: 10, width: '100%', padding: '9px', borderRadius: 8, border: '1.5px dashed #CBD5E1', background: '#F8FAFC', color: '#475569', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>＋ Agregar producto</button>

              <div style={{ marginTop: 14 }}>
                <label style={{ fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Motivo (queda en auditoría)</label>
                <textarea value={motivo} onChange={ev => setMotivo(ev.target.value)} rows={2} placeholder="Ej: la clienta pidió cambiar el lavavajilla; regalo de fidelización…"
                  style={{ width: '100%', marginTop: 5, padding: '8px 10px', borderRadius: 8, border: '1px solid #CBD5E1', fontSize: 12.5, fontFamily: 'inherit', resize: 'vertical' }} />
              </div>

              <div style={{ marginTop: 10, fontSize: 11, color: '#78350F', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 10px', lineHeight: 1.4 }}>
                Los regalos acá se ven internos para armar la caja. Al cliente se le muestra como <strong>“Regalo sorpresa”</strong>. El stock se descuenta igual al entregar.
              </div>

              {historial.length > 0 && (
                <details style={{ marginTop: 12 }}>
                  <summary style={{ fontSize: 11.5, color: '#64748B', fontWeight: 700, cursor: 'pointer' }}>Historial de cambios ({historial.length})</summary>
                  <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {historial.map((h, i) => (
                      <div key={i} style={{ fontSize: 11, color: '#475569', borderLeft: '2px solid #E2E8F0', paddingLeft: 8 }}>
                        <strong>{h.accion}</strong> {h.producto_original_sku ? `${h.producto_original_sku} → ` : ''}{h.producto_sku || ''} ×{h.cantidad}
                        {h.es_regalo ? ' 🎁' : ''}{Number(h.monto_delta) ? ` (+${fmt(h.monto_delta)})` : ''}
                        {h.motivo ? ` · ${h.motivo}` : ''}
                        <span style={{ color: '#94A3B8' }}> · {h.autor?.nombre || '—'}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </>
          )}
        </div>

        {pickerFor === undefined && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid #E2E8F0', background: '#F8FAFC', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 12, color: '#64748B' }}>
              Total: <strong style={{ color: '#1A3461' }}>{fmt(montoActual)}</strong>
              {diff.montoDelta !== 0 && <span> → <strong style={{ color: '#00A8B5' }}>{fmt(montoNuevo)}</strong> <span style={{ color: '#94A3B8' }}>(+{fmt(diff.montoDelta)})</span></span>}
              {diff.montoDelta === 0 && <span style={{ color: '#94A3B8' }}> · sin cambio de precio</span>}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={onClose} style={{ background: 'transparent', color: '#475569', border: '1.5px solid #E2E8F0', borderRadius: 8, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Cancelar</button>
              <button onClick={guardar} disabled={!hayCambios || guardando}
                style={{ background: hayCambios ? '#00A8B5' : '#CBD5E1', color: '#fff', border: 0, borderRadius: 8, padding: '7px 16px', fontSize: 12.5, fontWeight: 700, cursor: hayCambios ? 'pointer' : 'not-allowed', fontFamily: 'inherit', opacity: hayCambios ? 1 : 0.6 }}>
                {guardando ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/* Detalle de visita fallida en la tabla admin: motivo + Ver foto + Reagendar */
function NoEntregaCell({ entrega, onReagendado }) {
  const [abierto, setAbierto] = useState(false)
  const [fecha, setFecha] = useState(nextDateStr(entrega.fecha_programada))
  const [guardando, setGuardando] = useState(false)
  async function confirmar() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) { alert('Fecha inválida'); return }
    setGuardando(true)
    try {
      await reagendarEntrega(entrega.id, fecha)
      setAbierto(false)
      onReagendado()
    } catch (e) { alert('Error al reagendar: ' + e.message) } finally { setGuardando(false) }
  }
  return (
    <div style={{ marginTop: 6, fontSize: 11, lineHeight: 1.4 }}>
      <div style={{ color: '#991B1B', fontWeight: 600 }}>
        {entrega.motivo_no_entrega || 'Sin motivo'}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap', alignItems: 'center' }}>
        {entrega.foto_url && <VerFotoBtn path={entrega.foto_url} />}
        {!abierto ? (
          <button
            type="button"
            onClick={() => setAbierto(true)}
            style={{ background: '#1A3461', color: '#fff', border: 0, padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
          >
            🔄 Reagendar
          </button>
        ) : (
          <>
            <input
              type="date"
              value={fecha}
              min={todayIso()}
              onChange={ev => setFecha(ev.target.value)}
              style={{ padding: '3px 6px', borderRadius: 6, border: '1px solid #CBD5E1', fontSize: 11, fontFamily: 'inherit' }}
            />
            <button type="button" onClick={confirmar} disabled={guardando}
              style={{ background: '#00A8B5', color: '#fff', border: 0, padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
              {guardando ? '…' : 'OK'}
            </button>
            <button type="button" onClick={() => setAbierto(false)}
              style={{ background: 'transparent', color: '#64748B', border: '1px solid #CBD5E1', padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
              Cancelar
            </button>
          </>
        )}
      </div>
    </div>
  )
}

/* Botón inline que abre signed URL de foto en nueva pestaña */
function VerFotoBtn({ path }) {
  async function abrir() {
    const url = await getEntregaFotoSignedUrl(path)
    if (!url) { alert('No se pudo cargar la foto'); return }
    window.open(url, '_blank', 'noopener')
  }
  return (
    <button
      type="button"
      onClick={abrir}
      style={{ background: '#fff', color: '#1A3461', border: '1px solid #1A3461', padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
    >
      📷 Ver foto
    </button>
  )
}

function nextDateStr(fechaIso) {
  try {
    const d = new Date((fechaIso || todayIso()) + 'T12:00:00')
    d.setDate(d.getDate() + 1)
    return d.toISOString().slice(0, 10)
  } catch { return todayIso() }
}
function todayIso() {
  return new Date().toISOString().slice(0, 10)
}
